import { EAvailabilityMode, isAvailable } from "app/common/services/pageClaims";
import { swSettings } from "app/common/services/swSettings";
import { DEFAULT_HOMEPAGE_PREFERENCE_KEY } from "app/components/modals/constants";
import type CompanyIndustryType from "app/pages/sales-intelligence/types/industries";
import { is } from "app/pages/sales-intelligence/utils/type-guards";
import type { ICountryObject } from "app/services/CountryService";
import { PreferencesService } from "app/services/preferences/preferencesService";
import { userRoleTitle } from "app/UtilitiesAndConstants/UtilityFunctions/userRoleUtils";
import { SALES_HOMEPAGE } from "single-spa/react-app/router/router-otherwise-rules";

import type { SalesSettingsProvider, SalesSwSettings, SalesWorkspaceResources } from "./types";

const EMPTY_SALES_WORKSPACE_RESOURCES = {} as SalesWorkspaceResources;
const EMPTY_ALLOWED_COUNTRIES: ICountryObject[] = [];

const createSalesSettingsHelper = (
    salesSwSettings: SalesSwSettings,
    preferencesService: typeof PreferencesService,
): SalesSettingsProvider => {
    const getSalesWorkspaceResources = (): SalesWorkspaceResources => {
        return salesSwSettings.components?.SalesWorkspace?.resources ?? EMPTY_SALES_WORKSPACE_RESOURCES;
    };

    return {
        /**
         * Returns last supported snapshot date
         */
        getLastSnapshotDate(): string {
            return getSalesWorkspaceResources().SupportedDate;
        },
        getLastSupportedDate(showDaily: boolean) {
            const { LastSupportedDailyDate, SnapshotInterval } = getSalesWorkspaceResources();

            return showDaily ? LastSupportedDailyDate : SnapshotInterval.enddate;
        },
        /**
         * Returns initial country internal code
         */
        getInitialCountry(): number {
            return getSalesWorkspaceResources().InitialCountry;
        },
        getAllowedCountries(): ICountryObject[] {
            return salesSwSettings.components?.SalesWorkspace?.allowedCountries ?? EMPTY_ALLOWED_COUNTRIES;
        },
        /**
         * Checks if the user has only one country allowed
         */
        hasSingleCountryAllowed(): boolean {
            return this.getAllowedCountries().length === 1;
        },
        /**
         * Returns the sales track quota limit for the user
         */
        getQuotaLimit(): number {
            return getSalesWorkspaceResources().NumCompaniesTracked;
        },
        getCompanyIndustry(): CompanyIndustryType | null {
            return getSalesWorkspaceResources().Industry;
        },
        getFormattedCompanyIndustry(): CompanyIndustryType {
            return (this.getCompanyIndustry() ?? "").toLowerCase() as CompanyIndustryType;
        },

        getNumCompetitorsHookLimit(): number {
            return getSalesWorkspaceResources().NumCompetitorsHookLimit;
        },
        getNumTechnologiesHookLimit(): number {
            return getSalesWorkspaceResources().NumTechnologiesHookLimit;
        },

        getCompanyTechnologiesFeature(): EAvailabilityMode {
            return getSalesWorkspaceResources().CompanyTechnologiesFeature;
        },
        getCompanyCompetitorsFeature(): EAvailabilityMode {
            return getSalesWorkspaceResources().CompanyCompetitorsFeature;
        },
        getWebsiteTechnologiesFeature(): EAvailabilityMode {
            return getSalesWorkspaceResources().WebsiteTechnologiesFeature;
        },
        getWebsiteCompetitorsFeature(): EAvailabilityMode {
            return getSalesWorkspaceResources().WebsiteCompetitorsFeature;
        },

        hasSolution2(): boolean {
            return salesSwSettings.user.hasSolution2;
        },
        isSiUser(): boolean {
            return salesSwSettings.user.hasSI;
        },
        isSiUserWithSalesDefaultHomepage(): boolean {
            const { hasSI, hasMR, hasDM } = salesSwSettings.user;
            const packagesCount = [hasSI, hasMR, hasDM].filter(Boolean).length;
            const defaultHomepage = preferencesService.get(DEFAULT_HOMEPAGE_PREFERENCE_KEY) as string;
            return packagesCount === 1 ? hasSI : hasSI && defaultHomepage === SALES_HOMEPAGE;
        },
        hasSlAccess(): boolean {
            return salesSwSettings.user?.hasSlAccess;
        },
        hasCrmAccess(): boolean {
            return salesSwSettings.user?.hasCrmAccess;
        },
        hasApiSignalsAccess(): boolean {
            return salesSwSettings.user?.hasCrmAccess;
        },
        hasSignalsRestApiAccess(): boolean {
            return salesSwSettings.user?.hasSignalsRestApiAccess;
        },
        isAdmin(): boolean {
            return salesSwSettings.user.userRole === userRoleTitle.ADMIN;
        },
        isChampion(): boolean {
            return salesSwSettings.user.userRole === userRoleTitle.CHAMPION;
        },
        isInternalUser(): boolean {
            return Boolean(salesSwSettings.user?.isInternalUser);
        },
        get isCrmCustomFiltersEnabled(): boolean {
            return getSalesWorkspaceResources().SalesCustomCrmFiltersFeatureEnabled;
        },
        isMoreThenOneSolutionWithSi(): boolean {
            const { hasSI, hasMR, hasDM } = salesSwSettings.user;
            let numOfPackages = 0;

            if (hasSI) {
                numOfPackages = [hasSI, hasMR, hasDM].reduce((acc, value) => {
                    if (value) {
                        acc += 1;
                    }
                    return acc;
                }, 0);
            }

            return numOfPackages > 1;
        },
        isIntentSignalsEnabled(): boolean {
            return getSalesWorkspaceResources().IntentSignalsEnabled;
        },
        isSalesBusinessModelEnabled(): boolean {
            return getSalesWorkspaceResources().IsSalesBusinessModelEnabled;
        },
        getNumIntentTopics(): number {
            return salesSwSettings.components.SalesIntent?.resources?.NumIntentTopics ?? 0;
        },
        hasAccessToManageIntentTopicsForAdmin(): boolean {
            return this.isHighLevelAccount() && (this.isInternalUser() || this.isImpersonating());
        },
        isExcelAllowed(): boolean {
            return getSalesWorkspaceResources().IsExcelAllowed;
        },
        isHubspotEnabled(): boolean {
            return getSalesWorkspaceResources().HubspotIntegrationEnabled;
        },
        getAvailableRegions(): number[] {
            return getSalesWorkspaceResources().Regions || [];
        },
        isBenchmarksEnabled(): boolean {
            return getSalesWorkspaceResources().BenchmarksEnabled;
        },
        isDtcRetailerWebsiteTypeEnabled(): boolean {
            return getSalesWorkspaceResources().DtcRetailerWebsiteTypeEnabled;
        },
        isSiteTrendsEnabled(): boolean {
            return getSalesWorkspaceResources().SiteTrendsEnabled;
        },
        isCrmListEnabled(): boolean {
            return getSalesWorkspaceResources().CrmListEnabled;
        },
        crmFilterFieldsLimit(): number {
            return getSalesWorkspaceResources().CrmListFieldFiltersLimit;
        },
        hasTechnographicsAccess() {
            return !!salesSwSettings.components.Workspaces?.resources?.Technographics;
        },
        getTechnographicsLink(id: number | string): string {
            return `${salesSwSettings.components.Workspaces?.resources?.TechnographicsIconsPath}/id=${id}`;
        },
        isAssignToOtherUsersEnabled(): boolean {
            return getSalesWorkspaceResources().AssignLeadsToOtherUsersEnabled;
        },
        isSiteTagsEnabled(): boolean {
            return getSalesWorkspaceResources().SiteTagsEnabled;
        },
        isCsvEnrichmentEnabled(): boolean {
            return getSalesWorkspaceResources().CsvEnrichmentEnabled;
        },
        isCrmIntegrationsEnabled(): boolean {
            return getSalesWorkspaceResources().CrmIntegrationsEnabled;
        },
        isCompanyStructureEnabled(): boolean {
            return getSalesWorkspaceResources().CompanyPSEnabled;
        },
        isCompanyOrgTreeEnabled(): boolean {
            return getSalesWorkspaceResources().CompanyOrgTreeEnabled;
        },
        isTopicLeadersEnabled(): boolean {
            return getSalesWorkspaceResources().TopicLeadersEnabled;
        },
        isSignalsAIEnabled(): boolean {
            return getSalesWorkspaceResources().SignalsAIEnabled;
        },
        isHighLevelAccount(): boolean {
            return this.isAdmin() || this.isChampion();
        },
        isFro(): boolean {
            return salesSwSettings.components.Home.resources.IsCommonUser;
        },
        isImpersonating(): boolean {
            return salesSwSettings.components.Home.resources.Impersonates;
        },
        hasAccessToManageIntentTopics(): boolean {
            return getSalesWorkspaceResources().HasAccessToManageIntentTopics;
        },
        canDeleteOpportunities(): boolean {
            return getSalesWorkspaceResources().CanDeleteOpportunities;
        },
        isCompanyFocusedEmailAlerts(): boolean {
            return getSalesWorkspaceResources().SignalEmailsEnabled;
        },
        isAdsIntelPackagePresent(): boolean {
            const {
                resources: { HasAdsIntelPackage },
            } = salesSwSettings.components.WebsiteAdsIntelDisplay;

            return (
                !is.undefined(HasAdsIntelPackage) &&
                (Array.isArray(HasAdsIntelPackage) ? HasAdsIntelPackage[0] : HasAdsIntelPackage)
            );
        },
        isOtherSignalEventsEnabled(): boolean {
            // other signals functionality might be returned in the future
            return false;
        },

        getSalesApiSignalsDomainsLimit(): number {
            return getSalesWorkspaceResources().SalesApiSignalsDomainsLimit;
        },

        get isSIAppLeadGenEnabled(): boolean {
            return getSalesWorkspaceResources().SIAppLeadGenEnabled;
        },

        get totalCountries(): number[] {
            return getSalesWorkspaceResources().TotalCountries;
        },

        get isAiMeetingAgentEnabled(): boolean {
            return getSalesWorkspaceResources().AiMeetingAgentEnabled;
        },

        get isAiCraftEmailAgentLocked(): boolean {
            return getSalesWorkspaceResources().SIEmailAgentFeature === EAvailabilityMode.HOOK;
        },

        get isAiCraftEmailAgentHidden(): boolean {
            return getSalesWorkspaceResources().SIEmailAgentFeature === EAvailabilityMode.HIDDEN;
        },

        get isAiCraftEmailAgentEnabled(): boolean {
            return getSalesWorkspaceResources().SIEmailAgentFeature === EAvailabilityMode.OPEN;
        },

        get isAppsSdksAvailable(): boolean {
            return getSalesWorkspaceResources().AppIntTechnographicsEnabled;
        },

        get isSalesApiSignalsEnabled(): boolean {
            return getSalesWorkspaceResources().SalesApiSignalsEnabled;
        },

        get isContactsApiEnabled(): boolean {
            return getSalesWorkspaceResources().ContactsApiEnabled;
        },

        get isSalesApiSignalsIntentEnabled(): boolean {
            return getSalesWorkspaceResources().SalesApiSignalsIntentEnabled ?? false;
        },

        get isSalesApiSignalsRestEnabled(): boolean {
            return getSalesWorkspaceResources().SalesApiSignalsEnabled;
        },

        get isAppsOnly(): boolean {
            return (
                !getSalesWorkspaceResources().SalesSolution2Enabled &&
                getSalesWorkspaceResources().SalesIntelligenceAppsEnabled
            );
        },

        get isMarkettingChannelsAdonEnabled(): boolean {
            const { components } = salesSwSettings;
            return (
                isAvailable(components.WsWebSearchOverview) &&
                isAvailable(components.WsWebOrganicSearchCompetitors) &&
                isAvailable(components.WsWebSearchAds) &&
                isAvailable(components.WsWebDisplayTrafficOverview) &&
                isAvailable(components.WsWebDisplayTrafficVideos) &&
                isAvailable(components.WsWebSocialTrafficOverview)
            );
        },

        get isLeadGenAgentEnabled(): boolean {
            return getSalesWorkspaceResources().AiLeadGenAgentEnabled;
        },

        get isSalesforceEnrichmentEnabled() {
            return getSalesWorkspaceResources().SISalesforceEnrichmentEnabled;
        },

        get isSuperAgentEnabled(): boolean {
            return getSalesWorkspaceResources().SIAssistantAgentFeatureEnabled;
        },

        get isDataCreditsEnabled(): boolean {
            return getSalesWorkspaceResources().DataCreditsEnabled;
        },

        get isSIAdIntelligenceEnabled(): boolean {
            return getSalesWorkspaceResources().SIAdIntelligenceEnabled;
        },

        get isEcommConversionRateEnabled() {
            return getSalesWorkspaceResources().SIEcommConversionRate === EAvailabilityMode.OPEN;
        },

        get isConsolidatedAppsEnabled(): boolean {
            return getSalesWorkspaceResources().SIAppLeadGenEnabled || salesSwSettings.user?.hasSwAppIntelligence;
        },

        get isSavedSearchWebhooksEnabled(): boolean {
            return getSalesWorkspaceResources().SavedSearchWebhooksEnabled;
        },

        get hasAccessToManageSavedSearchWebhooks(): boolean {
            return getSalesWorkspaceResources().HasAccessToManageSavedSearchWebhooks;
        },

        get isSalesPlaybooksEnabled(): boolean {
            return getSalesWorkspaceResources().SalesPlaybookFeatureEnabled ?? false;
        },

        get isSiAdNetworksFeatureEnabled(): boolean {
            return getSalesWorkspaceResources().SiAdNetworksFeatureEnabled ?? false;
        },
    };
};

type SalesSettingsHelper = ReturnType<typeof createSalesSettingsHelper>;

let salesSettingsHelperCache: SalesSettingsHelper | undefined;

const getSalesSettingsHelper = (): SalesSettingsHelper => {
    if (!salesSettingsHelperCache) {
        salesSettingsHelperCache = createSalesSettingsHelper(swSettings as SalesSwSettings, PreferencesService);
    }

    return salesSettingsHelperCache;
};

const salesSettingsHelperTarget = {} as SalesSettingsHelper;

export default new Proxy(salesSettingsHelperTarget, {
    get(target, prop, receiver) {
        if (Reflect.has(target, prop)) {
            return Reflect.get(target, prop, receiver);
        }

        const helper = getSalesSettingsHelper();
        const value = Reflect.get(helper as object, prop, helper);

        return typeof value === "function" ? value.bind(helper) : value;
    },
    getOwnPropertyDescriptor(target, prop) {
        const targetDescriptor = Reflect.getOwnPropertyDescriptor(target, prop);
        if (targetDescriptor) {
            return targetDescriptor;
        }

        const helper = getSalesSettingsHelper();
        const descriptor = Reflect.getOwnPropertyDescriptor(helper, prop);
        if (!descriptor) {
            return undefined;
        }

        if ("value" in descriptor) {
            const { value } = descriptor;
            return {
                configurable: true,
                enumerable: descriptor.enumerable,
                value: typeof value === "function" ? value.bind(helper) : value,
                writable: true,
            };
        }

        return {
            configurable: true,
            enumerable: descriptor.enumerable,
            get: descriptor.get ? () => descriptor.get?.call(helper) : undefined,
            set: descriptor.set ? (value) => descriptor.set?.call(helper, value) : undefined,
        };
    },
    has(target, prop) {
        if (Reflect.has(target, prop)) {
            return true;
        }

        return prop in getSalesSettingsHelper();
    },
});
